import { DIContainer } from "../../../Global/Base/DIContainer";
import { Event } from "../../../Global/Base/Events";
import { EventsController } from "../../../Global/Dom/EventsController";
import { Localization } from "../../../OW.Remoting/Remoting/Localization";
import { registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { BreadcrumbNavItem } from "./BreadcrumbNavItem";
import { FormManager, IFormManager } from "./FormManager/IFormManager";
import { IHideCloseButtonChanged } from "./FormManager/IFormManagerInterfaces";
import { ISystemOperationRights, SystemOperation } from "./Helpers/SystemOperationRights";
import { IBreadcrumbNav } from "./IBreadcrumbNav";
import { UIManager } from "./UIManager";
import { IWindowable, WindowState } from "./UIManagerInterfaces";
import { IForm } from "./XGControls/IForm";

//TODO:[IFRAME] Drobeckova navigace je třeba reworkovat, a oddělat zavislost na IFormu, měl by to řidit form manager a mělo by se to řešit je na základě událostí
export class BreadcrumbNav extends HTMLElement implements IBreadcrumbNav {
    //#region Events
    /**
     * Event, který se vyvolá při zavolání metody dispose
     */
    public onDispose: Event<BreadcrumbNav, BreadcrumbNav> = new Event<BreadcrumbNav, BreadcrumbNav>();
    //#endregion Events

    //#region Fields
    /**
     * Položky v navigaci
     */
    public items: BreadcrumbNavItem[] = [];
    public allowViewMode: boolean;

    private _onlyPreviewMessageElement: HTMLElement;
    private _itemIdCounter: number = 0;
    private readonly _breadcrumbNavigationStyleClass = "breadcrumbnav";
    private _ignoreCloseEvent = false;
    private _canClose = true;
    /**
     * Chain promisů s probihajicimi updaty dat
     */
    private _waitForFormActualizeTask: Promise<void> = Promise.resolve();

    /**
     * Aktuálně zvolená položka v navigaci
     */
    private _selectedItem: BreadcrumbNavItem;

    public get element(): HTMLElement {
        return this;
    }

    private _eventsController = new EventsController();
    //#endregion Fields

    //#region Constructors
    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // Polyfill
    }

    public _constructor(): void {
        this._initialize();
    }
    //#endregion Constructors

    //#region Methods
    /**
     * Vytvoření drobečkové navigace
     */
    private async _initialize(): Promise<void> {
        this.classList.add(this._breadcrumbNavigationStyleClass);
        this.hide();

        this._eventsController.add(FormManager.instance.formHideCloseButtonChanged, (_sender: IFormManager, hideCloseButtonChanged: IHideCloseButtonChanged) => {
            this._canClose = !hideCloseButtonChanged.hideCloseButton;
        });
    }

    public hide(): void {
        this.element.style.display = "none";
    }

    public show(): void {
        this.element.style.display = "";
    }

    public async initialize(): Promise<void> {
        if (this.allowViewMode === undefined) this.allowViewMode = await DIContainer.instance.create(ISystemOperationRights).get(SystemOperation.ADVBRDCRMB);
    }

    /**
     * Přidání nového elementu do drobečkové navigace
     */
    public addNavigationItem(form: IWindowable): BreadcrumbNavItem {
        this._itemIdCounter++;
        const formName = form.getElement().getAttribute("data-name");
        let lockey = "GUI-508511"; // Formulář

        if (form.titleLocKey) {
            lockey = form.titleLocKey;
        }

        this._canClose = form instanceof IForm ? form.canClose : true;

        const item = new BreadcrumbNavItem(form, lockey, this.allowViewMode, formName, this._itemIdCounter);

        if (this.items.length > 0) {
            const lastBreadcrumbNavItem = this.items[this.items.length - 1];
            // Přepnutí formuláře do stavu readOnly
            lastBreadcrumbNavItem.setActiveState(false, true);
            if (lastBreadcrumbNavItem.form instanceof IForm) lastBreadcrumbNavItem.form.readOnly = true;
        }

        this.items.push(item);

        item.onSelectedEvent.add((_sender, args) => this._onSelectedItemMethod(args));
        item.onViewEvent.add((_sender, args) => this._onViewItemMethod(args));
        item.onCloseEvent.add(() => this.removeTopItem());

        form.closed.add(this._formCloseHandler.bind(this));

        this.appendChild(item.element);
        return item;
    }

    private _hideAllFormMenu(): void {
        for (const item of this.items) item.form.setState(WindowState.realVisibility, false);
    }

    private _formCloseHandler(sender: IWindowable): void {
        if (this._ignoreCloseEvent) return;
        for (const item of this.items) {
            if (item.form == sender) {
                this.removeItem(item);
                break;
            }
        }
    }

    /**
     * Metoda volana při kliku na položku drobečkové navigace
     */
    private async _onSelectedItemMethod(item: BreadcrumbNavItem): Promise<void> {
        if (!this._canClose && this.items.length > 0 && this.items[this.items.length - 1] !== item) {
            this._onViewItemMethod(item);
            return;
        }

        if (this._selectedItem) {
            this._selectedItem.setActiveState(false, !this._selectedItem.isLast);

            const indexOfNewlySelectedItem = this.items.findIndex(i => i.itemId === item.itemId);

            if (this.items.length > indexOfNewlySelectedItem && this._canClose) {
                UIManager.instance.showLoader();
                try {
                    for (let index = this.items.length - 1; index >= 0; index--) { // Zavírání od posledního otevřeného
                        const tempItem = this.items[index];
                        if (index > indexOfNewlySelectedItem) {
                            if (!(await tempItem.form.close())) {
                                this._ignoreCloseEvent = false;
                                item.setActiveState(false, true);
                                tempItem.showCurrentForm();
                                this._selectedItem = item;
                                return;
                            }
                        } else {
                            break;
                        }
                    }
                } finally {
                    UIManager.instance.hideLoader();
                }
            }
        }

        item.setActiveState(true, item.canClose);

        if (!item.canClose && this.items.length > 0 && this.items[this.items.length - 1] === item) {
            item.changeBreadcrumbNavItemIconType(null);
        }

        this._hideOnlyPreviewMessage();

        UIManager.instance.showLoader();
        try {
            if (item.form instanceof IForm) {
                this._showFormOnChange(item);
                item.form.readOnly = false;
            }
            this._selectedItem = item;
            this._hideAllFormMenu();
            this._selectedItem.form.setState(WindowState.realVisibility, true);
            if (item.form instanceof IForm) {
                await this._actualizeFormsAsync(item.form);
                await Promise.all((item.form).masterableFrames.map(frame => frame.rightsEvaluationTask));
            }
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Metoda slouží pro serializované volání asynchronní metody actualizeForm() na formuláři.
     * @param form Formulář, na kterém je žádaná aktualizace dat
     */
    private async _actualizeFormsAsync(form: IWindowable): Promise<void> {
        if (form instanceof IForm)
            this._waitForFormActualizeTask = this._waitForFormActualizeTask.then(() => form.actualizeForm());

        return this._waitForFormActualizeTask;
    }

    private async _onViewItemMethod(item: BreadcrumbNavItem): Promise<void> {
        if (this._selectedItem) {
            this._selectedItem.setActiveState(false);
        }
        if (item.form instanceof IForm) await this._actualizeFormsAsync(item.form);

        this._selectedItem = item;
        this._showOnlyPreviewMessage();

        this._showFormOnChange(item, true);
    }

    private _showOnlyPreviewMessage(): void {
        if (!this._onlyPreviewMessageElement) {
            this._createOnlyPreviewMessage();
        }
        this._onlyPreviewMessageElement.style.display = "";
        this._selectedItem.element.classList.add(`${this._breadcrumbNavigationStyleClass}__item--clickable`);
    }

    private _hideOnlyPreviewMessage(): void {
        if (!this._onlyPreviewMessageElement) {
            return;
        }
        this._onlyPreviewMessageElement.style.display = "none";
        this._selectedItem?.element.classList.remove(`${this._breadcrumbNavigationStyleClass}__item--clickable`);
    }

    private _createOnlyPreviewMessage(): void {
        this._onlyPreviewMessageElement = Localization.getTextElement("GUI-684136", "div"); // Zobrazuje se pouze náhled.
        this._onlyPreviewMessageElement.classList.add(this._breadcrumbNavigationStyleClass + "__preview-message");
        this._onlyPreviewMessageElement.style.display = "none";
        this.appendChild(this._onlyPreviewMessageElement);
    }

    private _showFormOnChange(eventItem: BreadcrumbNavItem, preview: boolean = false): void {
        for (const item of this.items) {
            const element = item.form.getElement()?.parentElement;
            if (item == eventItem) {
                if (preview || (!this._canClose && item !== this.items[this.items.length - 1])) {
                    item.form.setState(WindowState.previewActive, true);
                } else {
                    item.form.setState(WindowState.active, true);
                }

                element.classList.remove("inactive");
                element.classList.add("Switched");
                element.classList.add("TopLevel");
            } else {
                item.form.setState(WindowState.previewActive, false);
                element.classList.add("inactive");
                element.classList.remove("Switched");
                element.classList.remove("TopLevel");
            }
        }
    }

    /**
     * Odstranění posledního prvku z drobečkové navigace
     */
    public async removeTopItem(): Promise<void> {
        if (this._ignoreCloseEvent) {
            return;
        }
        const topItem = this.items[this.items.length - 1];
        this._ignoreCloseEvent = true;
        if (!await topItem.form.close()) {
            this._ignoreCloseEvent = false;
            return;
        }
        this._ignoreCloseEvent = false;
        topItem.dispose();
        this.items.pop();

        if (this._selectedItem === topItem) {
            this._selectedItem = null;
        }

        await this._afterRemoveItem();
    }

    public async removeItem(itemToRemove: BreadcrumbNavItem): Promise<void> {
        const i = this.items.findIndex((item) => item == itemToRemove);
        const item = this.items[i];
        item.dispose();
        this.items.splice(i, 1);

        if (this._selectedItem === item) {
            this._selectedItem = null;
        }

        await this._afterRemoveItem();
    }

    private async _afterRemoveItem(): Promise<void> {
        if (this.items.length > 0) {
            const form = this.items[this.items.length - 1].form;
            if (form instanceof IForm) form.readOnly = false;
            const newActiveItem = this.items[this.items.length - 1];
            newActiveItem.setNavItemActive();

            if (form instanceof IForm) await this._actualizeFormsAsync(form);

            this._selectedItem = newActiveItem;
        }
    }
    //#endregion Methods

    //#region Dispose
    /**
     * Destruktor
     */
    public dispose(): void {
        this.element.remove();
        this.items.forEach((item) => {
            const form = item.form;
            if (form instanceof IForm && form.getElement()) {
                form.readOnly = false;

                if (this.items.indexOf(item) == 0) {
                    this._actualizeFormsAsync(form);
                }
            }
            item.dispose();
        });

        this._eventsController?.dispose();
        this.onDispose.trigger(this, this);
        this.onDispose.removeAll();
    }
    //#endregion Dispose
}

registerCustomElement("tsc-breadcrumbnav", BreadcrumbNav);
